import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import WithdrawalsApiService from "@/core/services/api.service.withdrawals";

export default {
  props: {},
  name: "withdrawals",
  components: {},
  data() {
    return {
      pagination: {
        page: 1,
        itemsPerPage: 10
      },
      total: 0,
      withdrawOperations: [],
      operationsLoading: false,
      componentLoaded: false,
      activeTab: 0,
      tabs: [
        { name: "Active", status: "Processing" },
        { name: "Incorrect Data", status: "PendingPayment" },
        { name: "In progress", status: "SentToBank" },
        { name: "Cancelled", status: "ReturnedToWallet" },
        { name: "Paid", status: "Paid" }
      ],
      dateMenu: false,
      date: null,
      userIdFilter: "",
      emailFilter: "",
      selectedItems: [],
      selectAll: false,
      confirmDialog: false,
      confirmAction: null,
      confirmTitle: "",
      confirmText: "",
      actionLoading: false,
      tabCounts: {
        Processing: 0,
        SentToBank: 0,
        PendingPayment: 0,
        ReturnedToWallet: 0,
        Paid: 0
      }
    };
  },
  watch: {
    pagination: {
      handler() {
        if (!this.componentLoaded) return;
        this.getWithdrawOperations();
      },
      deep: true
    },
    activeTab() {
      if (!this.componentLoaded) return;
      this.pagination.page = 1;
      this.date = null; // Clear date filter when switching tabs
      this.userIdFilter = ""; // Clear userId filter
      this.emailFilter = ""; // Clear email filter
      this.withdrawOperations = []; // Reset withdraw items when changing tabs
      this.total = 0; // Reset total count
      this.selectedItems = []; // Clear selected items
      this.selectAll = false; // Reset select all
      this.getWithdrawOperations();
    },
    date() {
      if (!this.componentLoaded) return;
      this.pagination.page = 1;
      this.getWithdrawOperations();
    }
  },
  async mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: this.name }]);
    await WithdrawalsApiService.init();
    this.componentLoaded = true;
    this.getTabCounts();
    this.getWithdrawOperations();
  },
  methods: {
    getWithdrawOperations() {
      if (this.operationsLoading) return;

      const params = {
        page: this.pagination.page,
        maxCount: this.pagination.itemsPerPage
      };

      // Add status filter based on active tab
      const currentTab = this.tabs[this.activeTab];
      if (currentTab?.status) {
        params.status = currentTab.status;
      }

      // Add date filter if selected
      if (this.date) {
        params.date = this.date;
      }

      // Add userId filter
      if (this.userIdFilter) {
        params.userId = this.userIdFilter.trim();
      }

      // Add email filter
      if (this.emailFilter) {
        params.email = this.emailFilter.trim();
      }

      this.operationsLoading = true;
      WithdrawalsApiService.getWithdrawOperations(params)
        .then(res => {
          if (!res?.data) return;

          const { metadata, results } = res.data;
          this.withdrawOperations = results || [];
          this.total = metadata?.totalCount || 0;
        })
        .catch(error => {
          console.error("Error loading withdrawals:", error);
          this.$bvToast?.toast("Failed to load withdrawal operations", {
            variant: "danger",
            solid: true,
            autoHideDelay: 3000
          });
        })
        .finally(() => (this.operationsLoading = false));
    },
    formatDate(date) {
      if (!date) return "";
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      }).format(new Date(date));
    },
    formatAmount(amount) {
      if (!amount) return "0.00";
      return Number(amount).toFixed(2);
    },
    getStatusColor(status) {
      const statusColors = {
        Processing: "orange",
        SentToBank: "blue",
        PendingPayment: "amber",
        ReturnedToWallet: "red",
        Paid: "green"
      };
      return statusColors[status] || "grey";
    },
    getStatusText(status) {
      const statusTexts = {
        Processing: "Processing",
        SentToBank: "Sent To Bank",
        PendingPayment: "Pending Payment",
        ReturnedToWallet: "Returned To Wallet",
        Paid: "Paid"
      };
      return statusTexts[status] || status;
    },
    applyFilters() {
      this.pagination.page = 1;
      this.getWithdrawOperations();
    },
    clearUserIdFilter() {
      this.userIdFilter = "";
      this.pagination.page = 1;
      this.getWithdrawOperations();
    },
    clearEmailFilter() {
      this.emailFilter = "";
      this.pagination.page = 1;
      this.getWithdrawOperations();
    },
    clearDateFilter() {
      this.date = null;
      this.pagination.page = 1;
      this.getWithdrawOperations();
    },
    formatDateForApi(date) {
      if (!date) return null;
      return new Date(date).toISOString();
    },
    getTabIcon(status) {
      const statusIcons = {
        Processing: "mdi-clock-outline",
        SentToBank: "mdi-bank-transfer",
        PendingPayment: "mdi-timer-sand",
        ReturnedToWallet: "mdi-wallet-outline",
        Paid: "mdi-check-circle-outline"
      };
      return statusIcons[status] || "mdi-help-circle-outline";
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedItems = [...this.withdrawOperations.map(item => item.id)];
      } else {
        this.selectedItems = [];
      }
    },
    toggleItemSelection(itemId) {
      const index = this.selectedItems.indexOf(itemId);
      if (index > -1) {
        this.selectedItems.splice(index, 1);
      } else {
        this.selectedItems.push(itemId);
      }
      this.selectAll = this.selectedItems.length === this.withdrawOperations.length;
    },
    showConfirmDialog(action, itemId = null) {
      const isMarkAsPaid = action === "markAsPaid";
      const isBulk = itemId === null;
      const itemCount = isBulk ? this.selectedItems.length : 1;

      this.confirmAction = { action, itemId, isBulk };
      this.confirmTitle = isMarkAsPaid ? "Mark as Paid" : "Mark as Cancelled";
      this.confirmText = `Are you sure you want to ${
        isMarkAsPaid ? "mark" : "cancel"
      } ${itemCount} withdrawal operation${itemCount > 1 ? "s" : ""}?`;
      this.confirmDialog = true;
    },
    async executeAction() {
      if (!this.confirmAction) return;

      const { action, itemId, isBulk } = this.confirmAction;
      const operationIds = isBulk ? this.selectedItems : [itemId];

      if (operationIds.length === 0) return;

      this.actionLoading = true;

      try {
        if (action === "markAsPaid") {
          await WithdrawalsApiService.markWithdrawOperationAsPaid(operationIds);
        } else if (action === "cancel") {
          await WithdrawalsApiService.cancelWithdrawOperation(operationIds);
        }

        // Refresh data and clear selections
        this.selectedItems = [];
        this.selectAll = false;
        this.getWithdrawOperations();
        this.getTabCounts(); // Refresh tab counts after operations

        // Show success message
        const successMessage = `Successfully ${
          action === "markAsPaid" ? "marked as paid" : "cancelled"
        } ${operationIds.length} operation${operationIds.length > 1 ? "s" : ""}`;
        this.$bvToast?.toast(successMessage, {
          variant: "success",
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error("Action failed:", error);
        const errorMessage = `Failed to ${
          action === "markAsPaid" ? "mark as paid" : "cancel"
        } withdrawal operation${operationIds.length > 1 ? "s" : ""}`;
        this.$bvToast?.toast(errorMessage, {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.actionLoading = false;
        this.confirmDialog = false;
        this.confirmAction = null;
      }
    },
    cancelAction() {
      this.confirmDialog = false;
      this.confirmAction = null;
    },
    async exportWithdrawals() {
      // Show confirmation dialog
      const h = this.$createElement;
      const messageVNode = h("div", [
        h("p", { class: "mb-0 font-weight-bold" }, "This action can't be reverted!"),
        h("p", { class: "mb-0" }, "Please check everything before file exporting."),
        h("p", { class: "mb-0" }, "Selected operations will be replaced to the tab 'In Progress'.")
      ]);

      const confirmed = await this.$bvModal.msgBoxConfirm([messageVNode], {
        title: "Confirm Export",
        size: "md",
        buttonSize: "sm",
        okVariant: "primary",
        okTitle: "Export",
        cancelTitle: "Cancel",
        footerClass: "p-2",
        hideHeaderClose: false,
        centered: true
      });

      if (!confirmed) {
        return;
      }

      try {
        this.actionLoading = true;

        let response;
        response = await WithdrawalsApiService.getWithdrawFile(this.selectedItems);

        // Create blob and download file
        const blob = new Blob([response.data], { type: "application/xml" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const filename =
          this.selectedItems.length > 0
            ? `withdrawals_selected_${new Date().toISOString().split("T")[0]}.xml`
            : `withdrawals_all_${new Date().toISOString().split("T")[0]}.xml`;

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show success message
        const successMessage =
          this.selectedItems.length > 0
            ? `Successfully exported ${this.selectedItems.length} selected withdrawal${
                this.selectedItems.length > 1 ? "s" : ""
              }`
            : "Successfully exported all withdrawal records";

        this.$bvToast?.toast(successMessage, {
          variant: "success",
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error("Export failed:", error);
        this.$bvToast?.toast("Failed to export withdrawal data", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.actionLoading = false;
        this.getWithdrawOperations();
        this.getTabCounts();
      }
    },
    async moveToInProgress() {
      if (this.selectedItems.length === 0) return;

      this.actionLoading = true;
      try {
        await WithdrawalsApiService.moveToInProgress(this.selectedItems);

        const count = this.selectedItems.length;
        this.selectedItems = [];
        this.selectAll = false;
        this.getWithdrawOperations();
        this.getTabCounts();

        this.$bvToast?.toast(
          `Successfully moved ${count} operation${count > 1 ? "s" : ""} to In Progress`,
          {
            variant: "success",
            solid: true,
            autoHideDelay: 3000
          }
        );
      } catch (error) {
        console.error("Move to In Progress failed:", error);
        this.$bvToast?.toast("Failed to move operations to In Progress", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.actionLoading = false;
      }
    },
    async moveToPendingPayment() {
      if (this.selectedItems.length === 0) return;

      this.actionLoading = true;
      try {
        await WithdrawalsApiService.moveToPendingPayment(this.selectedItems);

        const count = this.selectedItems.length;
        this.selectedItems = [];
        this.selectAll = false;
        this.getWithdrawOperations();
        this.getTabCounts();

        this.$bvToast?.toast(
          `Successfully moved ${count} operation${count > 1 ? "s" : ""} to Incorrect Data`,
          {
            variant: "success",
            solid: true,
            autoHideDelay: 3000
          }
        );
      } catch (error) {
        console.error("Move to Incorrect Data failed:", error);
        this.$bvToast?.toast("Failed to move operations to Incorrect Data", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.actionLoading = false;
      }
    },
    async moveToInProgressSingle(itemId) {
      this.actionLoading = true;
      try {
        await WithdrawalsApiService.moveToInProgress([itemId]);
        this.getWithdrawOperations();
        this.getTabCounts();

        this.$bvToast?.toast("Successfully moved operation to In Progress", {
          variant: "success",
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error("Move to In Progress failed:", error);
        this.$bvToast?.toast("Failed to move operation to In Progress", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.actionLoading = false;
      }
    },
    async moveToPendingPaymentSingle(itemId) {
      this.actionLoading = true;
      try {
        await WithdrawalsApiService.moveToPendingPayment([itemId]);
        this.getWithdrawOperations();
        this.getTabCounts();

        this.$bvToast?.toast("Successfully moved operation to Incorrect Data", {
          variant: "success",
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error("Move to Incorrect Data failed:", error);
        this.$bvToast?.toast("Failed to move operation to Incorrect Data", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.actionLoading = false;
      }
    },
    async getTabCounts() {
      try {
        // Get counts for each tab status
        const statusList = [
          "Processing",
          "SentToBank",
          "PendingPayment",
          "ReturnedToWallet",
          "Paid"
        ];

        for (const status of statusList) {
          const params = {
            page: 1,
            maxCount: 1, // We only need the total count, not the actual data
            status: status
          };

          const response = await WithdrawalsApiService.getWithdrawOperations(params);
          if (response?.data?.metadata) {
            this.tabCounts[status] = response.data.metadata.totalCount || 0;
          }
        }
      } catch (error) {
        console.error("Error loading tab counts:", error);
        // Don't show toast for count errors as it's not critical
      }
    }
  },
  computed: {
    name() {
      return this.$i18n.t("menu.withdrawals") || "Withdrawals";
    },
    baseHeaders() {
      return [
        {
          text: "User ID",
          align: "left",
          value: "userId",
          sortable: false
        },
        {
          text: "User",
          align: "left",
          value: "user",
          sortable: false
        },
        {
          text: "Email",
          align: "left",
          value: "email",
          sortable: false
        },
        {
          text: "IBAN",
          align: "left",
          value: "iban",
          sortable: false
        },
        {
          text: "Amount",
          align: "left",
          value: "amount",
          sortable: false
        },
        {
          text: "Created At",
          align: "left",
          value: "createdAt",
          sortable: false
        },
        {
          text: "Status",
          align: "left",
          value: "status",
          sortable: false
        }
      ];
    },
    pendingPaymentHeaders() {
      return [
        {
          text: "",
          align: "left",
          value: "checkbox",
          sortable: false,
          width: "50px"
        },
        {
          text: "User ID",
          align: "left",
          value: "userId",
          sortable: false
        },
        {
          text: "User",
          align: "left",
          value: "user",
          sortable: false
        },
        {
          text: "Email",
          align: "left",
          value: "email",
          sortable: false
        },
        {
          text: "IBAN",
          align: "left",
          value: "iban",
          sortable: false
        },
        {
          text: "Amount",
          align: "left",
          value: "amount",
          sortable: false
        },
        {
          text: "Created At",
          align: "left",
          value: "createdAt",
          sortable: false
        },
        {
          text: "Status",
          align: "left",
          value: "status",
          sortable: false
        },
        {
          text: "Actions",
          align: "center",
          value: "actions",
          sortable: false,
          width: "100px"
        }
      ];
    },
    activeTabHeaders() {
      return [
        {
          text: "",
          align: "left",
          value: "checkbox",
          sortable: false,
          width: "50px"
        },
        {
          text: "User ID",
          align: "left",
          value: "userId",
          sortable: false
        },
        {
          text: "User",
          align: "left",
          value: "user",
          sortable: false
        },
        {
          text: "Email",
          align: "left",
          value: "email",
          sortable: false
        },
        {
          text: "IBAN",
          align: "left",
          value: "iban",
          sortable: false
        },
        {
          text: "Amount",
          align: "left",
          value: "amount",
          sortable: false
        },
        {
          text: "Created At",
          align: "left",
          value: "createdAt",
          sortable: false
        },
        {
          text: "Status",
          align: "left",
          value: "status",
          sortable: false
        },
        {
          text: "Actions",
          align: "center",
          value: "actions",
          sortable: false,
          width: "150px"
        }
      ];
    },
    inProgressHeaders() {
      return [
        {
          text: "",
          align: "left",
          value: "checkbox",
          sortable: false,
          width: "50px"
        },
        {
          text: "User ID",
          align: "left",
          value: "userId",
          sortable: false
        },
        {
          text: "User",
          align: "left",
          value: "user",
          sortable: false
        },
        {
          text: "Email",
          align: "left",
          value: "email",
          sortable: false
        },
        {
          text: "IBAN",
          align: "left",
          value: "iban",
          sortable: false
        },
        {
          text: "Amount",
          align: "left",
          value: "amount",
          sortable: false
        },
        {
          text: "Created At",
          align: "left",
          value: "createdAt",
          sortable: false
        },
        {
          text: "Status",
          align: "left",
          value: "status",
          sortable: false
        },
        {
          text: "Actions",
          align: "center",
          value: "actions",
          sortable: false,
          width: "150px"
        }
      ];
    },
    hasDateFilter() {
      return !!this.date;
    },
    currentTabName() {
      return this.tabs[this.activeTab]?.name || "";
    },
    hasSelectedItems() {
      return this.selectedItems.length > 0;
    },
    isInProgressTab() {
      return this.activeTab === 2;
    },
    exportButtonText() {
      return this.selectedItems.length > 0
        ? `Export selected records (${this.selectedItems.length})`
        : "Export all records";
    },
    tabsWithCounts() {
      return this.tabs.map(tab => ({
        ...tab,
        displayName: `${tab.name} (${this.tabCounts[tab.status] || 0})`
      }));
    }
  }
};
